import { ContactHelpLink } from '@/components/ContactHelpLink';
import { formatLongDate } from '@/lib/format-long-date';
import type { SuccessFeeTermsView } from '@fluenceur/types';
import { Button, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import { Logo } from '@fluenceur/ui';
import { Lock } from 'iconoir-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { LockedMigrationStep } from './LockedMigrationStep';
import { LockedStep } from './LockedStep';
import { LockedTermsStep } from './LockedTermsStep';
import { useLogoutFromLockedPage } from './use-logout-from-locked-page';

export interface SuccessFeeTermsLockedPageProps {
  /** The terms endpoint's answer; undefined while it is still in flight. */
  view: SuccessFeeTermsView | undefined;
  /** The instant read off a refused request, used until the view arrives. */
  fallbackDeadline: string | null;
}

/**
 * The dashboard, replaced, when the terms were not accepted in time.
 *
 * It opens by saying what this is not, because a merchant meeting a wall
 * assumes a payment problem and goes looking at its card. Then the two moves
 * that reopen it, in order, and the two ways out that are always available:
 * reaching a human, and leaving.
 */
export const SuccessFeeTermsLockedPage: React.FC<SuccessFeeTermsLockedPageProps> = ({
  view,
  fallbackDeadline,
}) => {
  const { t } = useTranslation();
  const { logout, isLoggingOut } = useLogoutFromLockedPage();

  const deadline = formatLongDate(view?.state.deadline ?? fallbackDeadline, t('common.locale'));

  return (
    <Flex direction="column" minH="100vh" w="full" bg="background" color="dark">
      <Flex justify="space-between" align="center" px={{ base: 5, md: '40px' }} py="24px">
        <Logo h="23px" color="dark" />
        <ContactHelpLink />
      </Flex>

      <Flex direction="column" flex="1" align="center" px={{ base: 4, md: 6 }} pb="56px">
        <VStack gap={6} align="stretch" maxW="640px" w="full">
          <VStack gap={3} align="center" textAlign="center">
            <Flex
              p="16px"
              borderRadius="full"
              bg="softCream"
              color="#777166"
              align="center"
              justify="center"
            >
              <Lock width={32} height={32} />
            </Flex>

            <Heading
              as="h1"
              fontFamily="heading"
              fontSize={{ base: '24px', md: '28px' }}
              fontWeight="600"
              letterSpacing="-0.02em"
              lineHeight="1.15"
              color="dark"
            >
              {t('legal.successFeeTerms.locked.title')}
            </Heading>

            <Text fontFamily="body" fontSize="15px" fontWeight="300" color="#777166">
              {deadline
                ? t('legal.successFeeTerms.locked.description', { date: deadline })
                : t('legal.successFeeTerms.locked.descriptionWithoutDate')}
            </Text>

            <Text fontFamily="body" fontSize="14px" color="#777166">
              {t('legal.successFeeTerms.locked.notAPayment')}
            </Text>
          </VStack>

          <LockedStep index={1} title={t('legal.successFeeTerms.locked.termsTitle')}>
            {view ? (
              <LockedTermsStep state={view.state} document={view.document} />
            ) : (
              <Text color="fg.muted">{t('legal.successFeeTerms.locked.loading')}</Text>
            )}
          </LockedStep>

          <LockedStep index={2} title={t('legal.successFeeTerms.locked.migrationTitle')}>
            <LockedMigrationStep />
          </LockedStep>

          <Flex justify="center" align="center" gap={4} wrap="wrap" pt={2}>
            <ContactHelpLink />
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              loading={isLoggingOut}
              loadingText={t('legal.successFeeTerms.locked.loggingOut')}
            >
              {t('legal.successFeeTerms.locked.logout')}
            </Button>
          </Flex>
        </VStack>
      </Flex>
    </Flex>
  );
};
